import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { GestureResponderEvent } from "@-/com.npmjs.react-native";

export const CardScrollOptInContext = createContext({
  enableScroll: () => {},
  isCard: false,
  isScrollEnabled: true,
});

export function useCardScrollOptIn() {
  return useContext(CardScrollOptInContext);
}

/** A completed tap opts in; touching down or abandoning a swipe never does. */
export function useCardScrollOptInState(presentationKey: string, canOptInFromTouch = true) {
  const [enabledCards, setEnabledCards] = useState<Readonly<Record<string, true>>>({});
  const touch = useRef<{ eligible: boolean; moved: boolean; x: number; y: number } | null>(null);
  const enableScroll = useCallback(() => {
    setEnabledCards((cards) =>
      cards[presentationKey] ? cards : { ...cards, [presentationKey]: true },
    );
  }, [presentationKey]);
  const value = useMemo(
    () => ({ enableScroll, isCard: true, isScrollEnabled: enabledCards[presentationKey] === true }),
    [enabledCards, enableScroll, presentationKey],
  );
  const trackMovement = (event: GestureResponderEvent) => {
    const start = touch.current;
    if (start !== null) {
      start.moved ||=
        Math.hypot(event.nativeEvent.pageX - start.x, event.nativeEvent.pageY - start.y) > 4;
    }
  };
  return {
    touchHandlers: {
      onTouchCancel: () => {
        touch.current = null;
      },
      onTouchEnd: (event: GestureResponderEvent) => {
        trackMovement(event);
        if (touch.current?.eligible && !touch.current.moved) {
          enableScroll();
        }
        touch.current = null;
      },
      onTouchMove: trackMovement,
      onTouchStart: (event: GestureResponderEvent) => {
        touch.current = {
          eligible: canOptInFromTouch,
          moved: false,
          x: event.nativeEvent.pageX,
          y: event.nativeEvent.pageY,
        };
      },
    },
    value,
  };
}
